/* src/app/(space)/contact/contact-client.tsx */

"use client";

import React, { useState, FormEvent } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import {
	Phone,
	Mail,
	MapPin,
	ExternalLink,
	ShoppingCart, // 购物车图标
	Send,
	Loader2,
} from "lucide-react";
import { useI18n } from "~/providers/i18n-client";

interface ContactClientProps {
	translations: {
		companyName: string;
		contactInfoTitle: string;
		person1: string;
		person2: string;
		emailLabel: string;
		addressTitle: string;
		addressLine: string;
		amapButtonText: string;
		googleMapButtonText: string;
		shopOnlineButtonText: string | null;
		shopOnlinePrompt: string | null;
		shopOnlineLink: string | null;
	};
}

// --- Form State Types ---
interface FormState {
	subject: string;
	email: string;
	name: string;
	company: string;
	message: string;
	terms: boolean;
}

interface FormFeedback {
	type: "success" | "error" | null;
	message: string;
}

// Map 组件动态引入 (SSR: false)
const Map = dynamic(() => import("./map"), {
	loading: () => <div className="bg-gray-200 animate-pulse w-full h-full" />,
	ssr: false,
});

export default function ContactClient({ translations }: ContactClientProps) {
	// --- Component State ---
	const [formState, setFormState] = useState<FormState>({
		subject: "",
		email: "",
		name: "",
		company: "",
		message: "",
		terms: false,
	});
	const [errors, setErrors] = useState<
		Partial<Record<keyof FormState, string>>
	>({});
	const [isVerifying, setIsVerifying] = useState(false);
	const [isVerified, setIsVerified] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [feedback, setFeedback] = useState<FormFeedback>({
		type: null,
		message: "",
	});

	const { locale } = useI18n();
	const isChinese = locale === "zh-CN";

	const position: [number, number] = [29.706373, 106.453158];
	const mapLink = isChinese
		? `https://uri.amap.com/marker?position=${position[1]},${position[0]}&name=重庆栢嘉电缆有限公司`
		: `https://www.google.com/maps?q=${position[0]},${position[1]}`;
	const mapLinkText = isChinese
		? translations.amapButtonText
		: translations.googleMapButtonText;

	// --- Form Logic ---
	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value, type } = e.target;
		const checked = (e.target as HTMLInputElement).checked;
		setFormState((prev) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));
		if (errors[name as keyof FormState]) {
			setErrors((prev) => ({ ...prev, [name]: undefined }));
		}
	};

	const handleVerification = () => {
		if (isVerifying || isVerified) return;
		setIsVerifying(true);
		setFeedback({ type: null, message: "" });
		const delay = 3000 + Math.random() * 4000; // 3-7 seconds
		setTimeout(() => {
			setIsVerifying(false);
			setIsVerified(true);
		}, delay);
	};

	const validateForm = (): boolean => {
		const newErrors: Partial<Record<keyof FormState, string>> = {};
		if (!formState.subject) newErrors.subject = "Subject is required.";
		if (!formState.name) newErrors.name = "Name is required.";
		if (!formState.email) {
			newErrors.email = "Email is required.";
		} else if (!/\S+@\S+\.\S+/.test(formState.email)) {
			newErrors.email = "Email format is invalid.";
		}
		if (!formState.message) {
			newErrors.message = "Message is required.";
		} else if (formState.message.length < 30) {
			newErrors.message = "Message must be at least 30 characters long.";
		}
		if (!formState.terms)
			newErrors.terms = "You must agree to the Privacy Policy.";

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setFeedback({ type: null, message: "" });
		if (!validateForm()) return;

		setIsSubmitting(true);
		try {
			const response = await fetch("/api/mail", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(formState),
			});
			const result = await response.json();
			if (!response.ok) {
				throw new Error(result.error || "An unexpected error occurred.");
			}
			setFeedback({
				type: "success",
				message: "Message sent! We will get back to you shortly.",
			});
			setFormState({
				subject: "",
				email: "",
				name: "",
				company: "",
				message: "",
				terms: false,
			});
			setIsVerified(false); // Reset captcha on success
		} catch (error: unknown) {
			let errorMessage = "An unexpected error occurred.";
			if (error instanceof Error) {
				errorMessage = `Failed to send message: ${error.message}`;
			}
			setFeedback({
				type: "error",
				message: errorMessage,
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<section className="py-20">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Top Section: Contact Info & Map */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
					<div className="flex flex-col space-y-8">
						{/* Company Name */}
						<div>
							<h2 className="text-3xl font-bold text-slate-800">
								{translations.companyName}
							</h2>
						</div>

						{/* Contact Persons */}
						<div>
							<h3 className="text-2xl font-bold text-gray-900 mb-4">
								{translations.contactInfoTitle}
							</h3>
							<div className="space-y-4 text-gray-700">
								<div className="flex items-center">
									<Phone className="w-5 h-5 mr-3 text-slate-700" />
									<span>{translations.person1}</span>
								</div>
								<div className="flex items-center">
									<Phone className="w-5 h-5 mr-3 text-slate-700" />
									<span>{translations.person2}</span>
								</div>
								<div className="flex items-center">
									<Mail className="w-5 h-5 mr-3 text-slate-700" />
									<span>{translations.emailLabel} contact@baijiacable.com</span>
								</div>
							</div>
						</div>

						{/* Address Section */}
						<div>
							<h3 className="text-2xl font-bold text-gray-900 mb-4">
								{translations.addressTitle}
							</h3>

							{/* ✅ 修改点：添加 flex flex-col items-start 强制子元素垂直排列 */}
							<div className="flex flex-col items-start space-y-4 text-gray-700">
								{/* 地址文本 */}
								<div className="flex items-start">
									<MapPin className="w-5 h-5 mr-3 text-slate-700 flex-shrink-0 mt-1" />
									<span>{translations.addressLine}</span>
								</div>

								{/* 地图链接 */}
								<a
									href={mapLink}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center text-slate-700 font-semibold hover:underline"
								>
									{mapLinkText}
									<ExternalLink className="w-4 h-4 ml-2" />
								</a>

								{/* 1688 店铺链接 (仅中文显示) */}
								{isChinese && (
									<a
										href="https://shop8575915j45658.1688.com/"
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center text-slate-700 font-semibold hover:underline"
									>
										1688 官方店铺
										<ExternalLink className="w-4 h-4 ml-2" />
									</a>
								)}
							</div>
						</div>
					</div>

					{/* Map Container */}
					<div className="h-[450px] w-full rounded-lg overflow-hidden shadow-lg border border-gray-200">
						<Map position={position} />
					</div>
				</div>

				{/* Online Shop Button Section (International/English) */}
				{translations.shopOnlineButtonText && translations.shopOnlineLink && (
					<div className="mt-16 flex flex-col md:flex-row items-center justify-center gap-x-8 gap-y-4 border-t border-gray-200 pt-12 text-center">
						<p className="text-gray-700 text-lg">
							{translations.shopOnlinePrompt}
						</p>
						<a
							href={translations.shopOnlineLink}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center justify-center px-6 py-3 bg-slate-800 !text-white font-semibold rounded-lg shadow-md hover:bg-slate-700 transition-colors duration-300 flex-shrink-0"
						>
							<ShoppingCart className="mr-2 h-5 w-5" />
							{translations.shopOnlineButtonText}
						</a>
					</div>
				)}

				{/* Contact Form Section (Non-Chinese) */}
				{!isChinese && (
					<div className="mt-16 text-left border-t border-gray-200 pt-12">
						<h3 className="text-3xl font-bold text-center text-gray-900 mb-8">
							Send us a Message
						</h3>
						<form
							onSubmit={handleSubmit}
							className="max-w-xl mx-auto space-y-6"
						>
							{/* ... Form input fields ... */}
							<div>
								<label
									htmlFor="subject"
									className="block text-sm font-medium text-gray-700 mb-1"
								>
									Subject
								</label>
								<input
									type="text"
									id="subject"
									name="subject"
									value={formState.subject}
									onChange={handleInputChange}
									className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 transition"
								/>
								{errors.subject && (
									<p className="text-red-500 text-xs mt-1">{errors.subject}</p>
								)}
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label
										htmlFor="email"
										className="block text-sm font-medium text-gray-700 mb-1"
									>
										Your Email
									</label>
									<input
										type="email"
										id="email"
										name="email"
										value={formState.email}
										onChange={handleInputChange}
										className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 transition"
									/>
									{errors.email && (
										<p className="text-red-500 text-xs mt-1">{errors.email}</p>
									)}
								</div>
								<div>
									<label
										htmlFor="name"
										className="block text-sm font-medium text-gray-700 mb-1"
									>
										Your Name
									</label>
									<input
										type="text"
										id="name"
										name="name"
										value={formState.name}
										onChange={handleInputChange}
										className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 transition"
									/>
									{errors.name && (
										<p className="text-red-500 text-xs mt-1">{errors.name}</p>
									)}
								</div>
							</div>
							<div>
								<label
									htmlFor="company"
									className="block text-sm font-medium text-gray-700 mb-1"
								>
									Company Name (Optional)
								</label>
								<input
									type="text"
									id="company"
									name="company"
									value={formState.company}
									onChange={handleInputChange}
									className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 transition"
								/>
							</div>
							<div>
								<label
									htmlFor="message"
									className="block text-sm font-medium text-gray-700 mb-1"
								>
									Message
								</label>
								<textarea
									id="message"
									name="message"
									rows={5}
									value={formState.message}
									onChange={handleInputChange}
									className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 transition"
								></textarea>
								{errors.message && (
									<p className="text-red-500 text-xs mt-1">{errors.message}</p>
								)}
							</div>

							{/* Terms Checkbox */}
							<div className="flex items-center gap-x-3">
								<input
									type="checkbox"
									id="terms"
									name="terms"
									checked={formState.terms}
									onChange={handleInputChange}
									className="h-4 w-4 text-slate-600 border-gray-300 rounded focus:ring-slate-500"
								/>
								<label htmlFor="terms" className="text-sm text-gray-600">
									I agree to the{" "}
									<span className="font-medium text-gray-800">
										Privacy Policy
									</span>
									.
								</label>
								{errors.terms && (
									<p className="text-red-500 text-xs ml-4">{errors.terms}</p>
								)}
							</div>

							{/* Final Actions: Verification & Submit */}
							<div className="border-t border-gray-200 pt-6 space-y-4">
								{/* --- Image-based Captcha --- */}
								<div className="flex justify-start">
									<div
										className="relative w-[302px] h-[76px]"
										onClick={handleVerification}
									>
										<Image
											src={
												isVerified
													? "/cloudflare-captcha-success.png"
													: "/cloudflare-captcha.png"
											}
											alt="Verification widget"
											className={
												!isVerified && !isVerifying
													? "cursor-pointer"
													: "cursor-default"
											}
											width={302}
											height={76}
											priority
										/>

										{isVerifying && (
											<div className="absolute top-[18px] left-[16px]">
												<div className="w-[30px] h-[30px] flex items-center justify-center bg-white rounded-sm shadow-inner">
													<Loader2 className="w-5 h-5 text-gray-700 animate-spin" />
												</div>
											</div>
										)}
									</div>
								</div>

								{feedback.message && (
									<p
										className={`text-center text-sm ${
											feedback.type === "success"
												? "text-green-600"
												: "text-red-600"
										}`}
									>
										{feedback.message}
									</p>
								)}

								{/* Submit Button */}
								<div className="text-center">
									<button
										type="submit"
										disabled={!isVerified || !formState.terms || isSubmitting}
										className="w-full inline-flex items-center justify-center px-8 py-3 bg-[#1D2C4A] text-black font-semibold rounded-lg shadow-md hover:bg-[#273a60] transition-colors duration-300 disabled:bg-gray-400 disabled:text-gray-600 disabled:cursor-not-allowed"
									>
										{isSubmitting ? (
											<Loader2 className="mr-2 h-5 w-5" />
										) : (
											<Send className="mr-2 h-5 w-5" />
										)}
										{isSubmitting ? "Sending..." : "Submit Message"}
									</button>
								</div>
							</div>
						</form>
					</div>
				)}
			</div>
		</section>
	);
}
